import React, { useRef, useEffect } from 'react';

const SpawnerPreview: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;
    let containerRadius = 150; // Will be updated in resize

    // Ball Class
    interface Ball {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      color: string;
      id: number;
    }

    let balls: Ball[] = [];
    const colors = ['#00e5ff', '#d500f9', '#ffea00', '#ff0080', '#00ff00', '#ff9100'];
    let ballIdCounter = 0;
    const collisionCooldown = new Map<string, number>();
    let lastGlobalSpawnTime = 0; // Added to prevent mass spawning

    const spawnBall = (x: number, y: number, vx: number, vy: number) => {
      const newBall: Ball = {
        x,
        y,
        vx,
        vy,
        radius: 6 + Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        id: ballIdCounter++
      };
      balls.push(newBall);
    };

    const resetSim = () => {
      balls = [];
      ballIdCounter = 0;
      // Start with 2 balls
      spawnBall(-50, 0, (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);
      spawnBall(50, 0, (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);
    };

    resetSim();

    const resize = () => {
      if (containerRef.current && canvas) {
        const { clientWidth, clientHeight } = containerRef.current;
        width = clientWidth;
        height = clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        centerX = width / 2;
        centerY = height / 2;
        
        // Dynamic radius: standardized to 0.8 to match Escape mode feel
        containerRadius = (Math.min(width, height) / 2) * 0.8;
      }
    };

    window.addEventListener('resize', resize);
    resize();

    const update = () => {
      const gravity = 0; // No gravity for this specific spawner logic to keep balls bouncing everywhere
      const friction = 1.0; // Perfect elastic movement
      const maxBalls = 30;

      if (balls.length >= maxBalls) {
        resetSim();
        return;
      }

      // Update Physics
      for (let i = 0; i < balls.length; i++) {
        const ball = balls[i];
        ball.x += ball.vx;
        ball.y += ball.vy;

        // Container Collision
        const dist = Math.sqrt(ball.x * ball.x + ball.y * ball.y);
        if (dist + ball.radius > containerRadius) {
          const nx = ball.x / dist;
          const ny = ball.y / dist;
          const dot = ball.vx * nx + ball.vy * ny;
          ball.vx = (ball.vx - 2 * dot * nx);
          ball.vy = (ball.vy - 2 * dot * ny);
          const overlap = dist + ball.radius - containerRadius;
          ball.x -= nx * overlap;
          ball.y -= ny * overlap;
        }

        // Ball-Ball Collision
        for (let j = i + 1; j < balls.length; j++) {
          const other = balls[j];
          const dx = other.x - ball.x;
          const dy = other.y - ball.y;
          const dSq = dx * dx + dy * dy;
          const minDist = ball.radius + other.radius;

          if (dSq < minDist * minDist) {
            const pairKey = ball.id < other.id ? `${ball.id}-${other.id}` : `${other.id}-${ball.id}`;
            const now = Date.now();
            
            // Collision response (Elastic)
            const d = Math.sqrt(dSq);
            const nx = dx / d;
            const ny = dy / d;
            const p = (ball.vx * nx + ball.vy * ny - other.vx * nx - other.vy * ny) / (1 + 1);
            
            ball.vx -= p * nx;
            ball.vy -= p * ny;
            other.vx += p * nx;
            other.vy += p * ny;

            // Separate
            const overlap = minDist - d;
            ball.x -= nx * overlap / 2;
            ball.y -= ny * overlap / 2;
            other.x += nx * overlap / 2;
            other.y += ny * overlap / 2;

            // Spawn on collision with strict throttles
            if (balls.length < maxBalls) {
              const lastPairSpawn = collisionCooldown.get(pairKey) || 0;
              const globalCooldown = 300; // ms
              const pairCooldown = 1500; // ms
              
              if (now - lastPairSpawn > pairCooldown && now - lastGlobalSpawnTime > globalCooldown) {
                const spawnX = (ball.x + other.x) / 2;
                const spawnY = (ball.y + other.y) / 2;
                
                // Only spawn ONE ball
                spawnBall(spawnX, spawnY, -nx * 3, -ny * 3);
                
                // Set cooldowns
                collisionCooldown.set(pairKey, now);
                lastGlobalSpawnTime = now;
              }
            }
          }
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      
      ctx.save();
      ctx.translate(centerX, centerY);

      // Draw Container with vibrant gradient
      ctx.beginPath();
      ctx.arc(0, 0, containerRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-containerRadius, -containerRadius, containerRadius, containerRadius);
      grad.addColorStop(0, '#ffea00');
      grad.addColorStop(0.5, '#ff0080');
      grad.addColorStop(1, '#00e5ff');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw Balls with glow
      balls.forEach(ball => {
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
        ctx.fillStyle = ball.color;
        ctx.fill();
      });

      ctx.restore();
    };

    const loop = () => {
      update();
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="group relative rounded-[3rem] overflow-hidden aspect-square glass-amber border-none hover:glow-amber transition-all duration-500 shadow-2xl">
      <canvas ref={canvasRef} className="block" />
      
      {/* Overlay Text matching SimulationCard */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white to-transparent pointer-events-none">
        <h3 className="text-black font-bold text-lg uppercase tracking-tight">SPAWNER</h3>
        <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Dynamic Agent Generation</p>
      </div>
    </div>
  );
};

export default SpawnerPreview;
